import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";

// Skill theme configurations based on the design spec
export interface SkillTheme {
  id: string;
  name: string;
  emotion: string;
  colors: {
    primary: string;
    accent: string;
    background: string;
    border: string;
    glow?: string;
  };
  typography: {
    headings: string;
    body: string;
    special?: string;
  };
  motion: {
    duration: string;
    easing: string;
    interaction: string;
  };
  languageTokens: string[];
  uniqueElement: string;
}

const skillThemes: Record<string, SkillTheme> = {
  "web-development": {
    id: "web-development",
    name: "Builder World",
    emotion: "Focused · Logical · Constructive",
    colors: {
      primary: "217 91% 60%", // #3B82F6 Builder Blue
      accent: "187 85% 53%",  // #22D3EE Cyan
      background: "222 47% 11%", // #0B1220
      border: "217 19% 27%", // #1F2937
    },
    typography: {
      headings: "'Inter', 'SF Pro', sans-serif",
      body: "'Inter', sans-serif",
      special: "'JetBrains Mono', monospace",
    },
    motion: {
      duration: "120ms",
      easing: "ease-out",
      interaction: "snap, precise",
    },
    languageTokens: ["Build", "Fix", "Deploy", "Structure", "Break", "Repair"],
    uniqueElement: "Live Preview Pane",
  },
  "public-speaking": {
    id: "public-speaking",
    name: "Stage World",
    emotion: "Calm · Presence · Confidence",
    colors: {
      primary: "38 92% 50%", // #F59E0B Spotlight Amber
      accent: "45 93% 58%",  // #FCD34D
      background: "0 0% 4%", // #0A0A0A
      border: "38 80% 25%",
      glow: "38 92% 50% / 0.25", // Amber glow
    },
    typography: {
      headings: "'Poppins', sans-serif",
      body: "'Inter', sans-serif",
      special: "'Inter Mono', monospace",
    },
    motion: {
      duration: "350ms",
      easing: "ease-in-out",
      interaction: "soft fade, breathe-like",
    },
    languageTokens: ["Speak", "Pause", "Connect", "Presence", "Listen"],
    uniqueElement: "Stage Timer",
  },
  "video-editing": {
    id: "video-editing",
    name: "Creator World",
    emotion: "Creative · Flow · Transformation",
    colors: {
      primary: "271 91% 65%", // #8B5CF6 Creator Purple
      accent: "330 81% 60%", // #EC4899 Pink
      background: "253 54% 8%", // #0E0B1F
      border: "271 50% 30%",
      glow: "271 91% 65% / 0.12",
    },
    typography: {
      headings: "'Manrope', sans-serif",
      body: "'Inter', sans-serif",
      special: "'Manrope', sans-serif",
    },
    motion: {
      duration: "250ms",
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      interaction: "smooth slide, elastic",
    },
    languageTokens: ["Cut", "Flow", "Pace", "Transform", "Style"],
    uniqueElement: "Before ↔ After slider",
  },
  "entrepreneurship": {
    id: "entrepreneurship",
    name: "Decision World",
    emotion: "Bold · Strategic · Thinking",
    colors: {
      primary: "160 84% 39%", // #10B981 Decision Green
      accent: "160 64% 52%", // #34D399
      background: "222 47% 2%", // #020617
      border: "162 72% 17%", // #064E3B
    },
    typography: {
      headings: "'IBM Plex Sans', sans-serif",
      body: "'Inter', sans-serif",
      special: "'IBM Plex Sans', sans-serif",
    },
    motion: {
      duration: "200ms",
      easing: "ease-in",
      interaction: "firm, confirm-style",
    },
    languageTokens: ["Decide", "Risk", "Test", "Outcome", "Learn"],
    uniqueElement: "Scenario Cards",
  },
};

const defaultTheme: SkillTheme = {
  id: "default",
  name: "Default World",
  emotion: "Curious · Open · Learning",
  colors: {
    primary: "168 76% 42%",
    accent: "282 44% 53%",
    background: "230 35% 7%",
    border: "230 25% 20%",
  },
  typography: {
    headings: "'Poppins', sans-serif",
    body: "'Inter', sans-serif",
  },
  motion: {
    duration: "200ms",
    easing: "ease-out",
    interaction: "smooth",
  },
  languageTokens: ["Learn", "Grow", "Practice", "Master"],
  uniqueElement: "Progress Tracker",
};

interface SkillThemeContextType {
  currentTheme: SkillTheme;
  selectedSkill: string | null;
  setSelectedSkill: (skill: string | null) => void;
  getThemeStyles: () => Record<string, string>;
  isTransitioning: boolean;
}

const SkillThemeContext = createContext<SkillThemeContextType | null>(null);

export const SkillThemeProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSkill, setSelectedSkillState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem("selected_skill");
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentTheme = selectedSkill ? (skillThemes[selectedSkill] || defaultTheme) : defaultTheme;

  // Apply theme CSS variables when theme changes
  useEffect(() => {
    if (!selectedSkill) return;

    setIsTransitioning(true);
    const theme = skillThemes[selectedSkill] || defaultTheme;
    const root = document.documentElement;

    // Apply skill-specific CSS variables
    root.style.setProperty('--skill-primary', theme.colors.primary);
    root.style.setProperty('--skill-accent', theme.colors.accent);
    root.style.setProperty('--skill-background', theme.colors.background);
    root.style.setProperty('--skill-border', theme.colors.border);
    if (theme.colors.glow) {
      root.style.setProperty('--skill-glow', theme.colors.glow);
    }
    root.style.setProperty('--skill-motion-duration', theme.motion.duration);
    root.style.setProperty('--skill-motion-easing', theme.motion.easing);

    // Add transition class to body for smooth color shifts
    document.body.classList.add('skill-transitioning');

    // Remove transition class after animation
    const timer = setTimeout(() => {
      setIsTransitioning(false);
      document.body.classList.remove('skill-transitioning');
    }, 350);

    return () => clearTimeout(timer);
  }, [selectedSkill]);

  const setSelectedSkill = useCallback((skill: string | null) => {
    setSelectedSkillState(skill);
    if (skill) {
      localStorage.setItem("selected_skill", skill);
    } else {
      localStorage.removeItem("selected_skill");
    }
  }, []);

  const getThemeStyles = useCallback(() => {
    const theme = currentTheme;
    return {
      '--theme-primary': `hsl(${theme.colors.primary})`,
      '--theme-accent': `hsl(${theme.colors.accent})`,
      '--theme-background': `hsl(${theme.colors.background})`,
      '--theme-border': `hsl(${theme.colors.border})`,
      '--theme-glow': theme.colors.glow ? `hsl(${theme.colors.glow})` : 'transparent',
    };
  }, [currentTheme]);

  // Sync with storage events
  useEffect(() => {
    const handleStorage = () => {
      const skill = localStorage.getItem("selected_skill");
      setSelectedSkillState(skill);
    };
    
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <SkillThemeContext.Provider
      value={{
        currentTheme,
        selectedSkill,
        setSelectedSkill,
        getThemeStyles,
        isTransitioning,
      }}
    >
      {children}
    </SkillThemeContext.Provider>
  );
};

export const useSkillTheme = () => {
  const context = useContext(SkillThemeContext);
  if (!context) {
    throw new Error("useSkillTheme must be used within a SkillThemeProvider");
  }
  return context;
};

export const useSkillThemeSafe = () => {
  return useContext(SkillThemeContext);
};

export { skillThemes, defaultTheme };